import React from 'react';

// Design System Components
import Dialog from 'Components/DesignSystem/Dialog/Dialog';
import { DialogSize } from 'types/DesignSystem/Dialog';
import { FOCUS_RING } from 'Components/DesignSystem/focusRing';
import { ChevronLeft } from 'Components/DesignSystem/Iconography/Icons';

interface ModalWrapperProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  backButton?: boolean;
  showCloseButton?: boolean;
  width?: 'sm' | 'md' | 'lg' | 'xl' | 'normal';
}

/**
 * ModalWrapper — a thin adapter over the design system's `Dialog`.
 *
 * ## Why it still exists
 *
 * Nine modals in this module pass `isOpen / onClose / title / footer / width`, two of
 * them also `backButton` + `showCloseButton`. Rewriting all nine at their call sites
 * would have been nine chances to change behaviour in a change whose entire point is
 * that behaviour does not change. Keeping the prop surface and swapping the body means
 * the migration is one file. Same shape as `Components/Breadcrumb.tsx` over
 * `Breadcrumbs`.
 *
 * ## What the hand-rolled version was missing (2026-08-30)
 *
 * All of it silent — every one of these is something a user could hit and no test could:
 *
 * - **No `Escape`.** No key handler existed anywhere in the file. Nine modals you could
 *   only leave with the mouse.
 * - **No focus trap and no focus restore.** Tab walked straight out of the modal into
 *   the page behind it, and closing dropped focus on `<body>` rather than returning it
 *   to whatever opened the modal.
 * - **No `role="dialog"`, no `aria-modal`, no `aria-labelledby`.** The title was a
 *   heading in an anonymous `div`; nothing tied it to the surface.
 * - **`role="button"` on the panel itself** — added to host a click-swallowing handler,
 *   with the result that a screen reader announced the entire dialog surface as a
 *   button. `Dialog` stops propagation without borrowing a role to do it.
 * - No scroll lock, so the page behind scrolled under the modal.
 *
 * `useDismissable`, which `Dialog` uses, supplies all of it — and is already
 * stack-aware, which matters here more than anywhere else in the app (see below).
 *
 * ## Nesting, which this module does twice
 *
 * `AttributeValuesModal` is a `Dialog`, and the "Create New Value" link inside it opens
 * `CreateAttributeValueModal` **while it stays mounted** — the value created there
 * routes back into the parent's chip list. `CreateNewProductModal` does the same with
 * its own child. The hand-rolled version handled this with a hardcoded `z-[60]` and a
 * comment explaining that it had to out-rank `Dialog`'s `z-50`.
 *
 * That stops being a layering problem and becomes a *layer* problem once both are
 * `Dialog`s, and `useDismissable` already models it: a LIFO stack where only the
 * topmost layer answers `Escape`, only the topmost cycles Tab (the covered panel used
 * to yank focus back on every keypress), and the scroll lock is refcounted so the inner
 * dialog closing does not unlock the page under the outer one. Both dialogs sit at
 * `z-50` now and the child paints above the parent by DOM order — verified in Chromium
 * with `elementFromPoint`, not by reading the classes, because equal z-index is
 * resolved by DOM order and a "correct" class set can still be invisible.
 *
 * ## Width
 *
 * `normal` was `max-w-[50vw] min-w-[480px]` and is `Dialog size="half"`, which was added
 * for it (DS § 179, as `xl`; renamed `half` in § 345 because it was narrower than `lg`,
 * with the same classes) so four screens do not silently resize. `sm` maps exactly (384px).
 * `md` moves 448px → 490px, the Figma spec — the one deliberate geometry change here,
 * and the reason it is deliberate is that 448 was `max-w-md`, a Tailwind default nobody
 * chose.
 */
const WIDTH_TO_SIZE: Record<NonNullable<ModalWrapperProps['width']>, DialogSize> = {
  sm: 'sm',
  md: 'md',
  /* `max-w-[50vw] min-w-[480px]`, preserved exactly (DS § 345 renamed `xl` → `half`). */
  normal: 'half',
  lg: 'lg',
  xl: 'lg',
};

const ModalWrapper = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  width = 'md',
  backButton = false,
  showCloseButton = true,
}: ModalWrapperProps) => (
  <Dialog
    isOpen={isOpen}
    onClose={onClose}
    title={title}
    footer={footer}
    size={WIDTH_TO_SIZE[width]}
    showCloseButton={showCloseButton}
    /*
      The backdrop stays click-to-dismiss, as it was. These are pickers and short
      forms, not the long editors `closeOnOutsideClick={false}` exists to protect —
      taking it away would be a silent regression for users who already close them
      that way.
    */
    closeOnOutsideClick
    data-testid="catalog-modal"
    headerLeading={backButton ? (
      <button
        type="button"
        onClick={onClose}
        data-testid="catalog-modal-back-btn"
        /* `p-1.5` for a 32px target (SC 2.5.8 asks 24) and a real focus ring — the
           hand-rolled back button had `p-1` and no ring at all. */
        className={`tw-p-1.5 tw-rounded-full tw-text-ink-tertiary hover:tw-bg-surface-hover-strong hover:tw-text-ink tw-transition-colors ${FOCUS_RING}`}
        aria-label="Go back"
      >
        {/* DS ChevronLeft draws in currentColor, so the button's role ink
            actually paints it — LeftVectorIcon hardcoded stroke #172B4D and was
            invisible on the dark ground (the Lists back-arrow bug, same glyph). */}
        <ChevronLeft size={20} />
      </button>
    ) : undefined}
  >
    {children}
  </Dialog>
);

export default ModalWrapper;
